import React, { useState } from 'react';
import { POPULAR_BANKS } from '../../constants';
import type { BankItem } from '../../constants';
import { Search, ChevronRight } from 'lucide-react';
import { cn } from '@/shared/utils/cn';

interface NetBankingProps {
  onSelectBank: (bankCode: string, bankName: string) => void;
  selectedBankCode?: string;
  isMobileView?: boolean;
  banks?: BankItem[] | null;
}

export const NetBanking: React.FC<NetBankingProps> = ({
  onSelectBank,
  selectedBankCode,
  isMobileView = false,
  banks,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const popularBanks = POPULAR_BANKS;
  const popularBankCodes = new Set(POPULAR_BANKS.map((bank) => bank.code));
  const otherBanks = banks?.filter((bank) => !popularBankCodes.has(bank.code)) || [];

  const filteredOtherBanks = otherBanks.filter((bank) =>
    bank.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isMobileView) {
    return (
      <div className="w-full bg-white">
        <h3 className="mb-4 text-lg font-extrabold leading-none text-[#4A4A4A]">Popular Banks</h3>

        <div className="mb-6 grid grid-cols-6 gap-4">
          {popularBanks.map((bank) => {
            const isSelected = selectedBankCode === bank.code;

            return (
              <button
                key={bank.code}
                type="button"
                onClick={() => onSelectBank(bank.code, bank.name)}
                className={cn(
                    'flex min-w-0 flex-col items-center gap-2 text-center transition-opacity hover:opacity-80',
                  isSelected && 'opacity-90'
                )}
              >
                <span
                  className={cn(
                    'flex h-12 w-12 items-center justify-center bg-[#DDF0FF]',
                    isSelected && 'ring-2 ring-[#011E38] ring-offset-2'
                  )}
                >
                  <bank.Icon className="h-8 w-8 object-contain" />
                </span>
                <span className="text-base font-medium leading-none text-[#4A4A4A]">
                  {bank.name}
                </span>
              </button>
            );
          })}
        </div>

        <div className="relative mb-5 w-full">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8C8C8C]">
            <Search className="h-6 w-6" strokeWidth={2.8} />
          </span>
          <input
            type="text"
            placeholder="Search bank"
            className="h-12 w-full rounded-[5px] border border-[#93A2B3] bg-white pl-12 pr-4 text-sm font-medium tracking-[0.02em] text-[#011E38] outline-none placeholder:text-[#666666] focus:border-[#6F7F91]"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="rounded-[5px] border border-[#E2E2E2] bg-white px-5 py-4">
          <h3 className="mb-4 text-lg font-extrabold leading-none text-[#4A4A4A]">Other Banks</h3>
          <div className="max-h-[150px] overflow-y-auto pr-2">
            {filteredOtherBanks.length > 0 ? (
              filteredOtherBanks.map((bank) => {
                const isSelected = selectedBankCode === bank.code;

                return (
                  <button
                    key={bank.code}
                    type="button"
                    onClick={() => onSelectBank(bank.code, bank.name)}
                    className={cn(
                      'block w-full rounded px-0 py-2 text-left text-sm font-medium leading-tight tracking-[0.02em] transition-colors',
                      isSelected ? 'text-[#011E38]' : 'text-[#4A4A4A] hover:text-[#011E38]'
                    )}
                  >
                    {bank.name}
                  </button>
                );
              })
            ) : (
              <div className="py-6 text-center text-base text-gray-400">
                No banks matching "{searchTerm}"
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col bg-[#FEFFF9] rounded-lg">
      <div className="relative mb-5 w-full">
        <span className="absolute left-3.5 top-[13px] text-gray-400">
          <Search className="h-4 w-4" />
        </span>
        <input
          type="text"
          placeholder="Search bank"
          className="w-full border border-gray-200 rounded-md pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#011E38] font-medium"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="mb-6">
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3.5">Popular Banks</h3>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {popularBanks.map((bank) => {
            const isSelected = selectedBankCode === bank.code;
            return (
              <button
                key={bank.code}
                onClick={() => onSelectBank(bank.code, bank.name)}
                className={cn(
                  'flex flex-col items-center justify-center p-3 border rounded-lg transition-all',
                  isSelected
                    ? 'border-[#011E38] bg-[#F6FAFF] shadow-sm'
                    : 'border-[#00000017] hover:border-gray-300 bg-white'
                )}
              >
                <span className="h-7 w-7 mb-1.5 flex items-center justify-center">
                  <bank.Icon className="h-full w-full object-contain" />
                </span>
                <span className="text-xs font-semibold text-gray-700">{bank.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="h-[1px] bg-gray-100 mb-5" />

      <div>
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Other Banks</h3>
        <div className="max-h-[300px] overflow-y-auto divide-y divide-gray-100 pr-1 border border-gray-100 rounded-lg bg-white">
          {filteredOtherBanks.length > 0 ? (
            filteredOtherBanks.map((bank) => {
              const isSelected = selectedBankCode === bank.code;
              return (
                <button
                  key={bank.code}
                  onClick={() => onSelectBank(bank.code, bank.name)}
                  className={cn(
                    'w-full flex items-center justify-between px-4 py-3.5 text-left text-sm font-medium transition-colors',
                    isSelected
                      ? 'bg-[#F6FAFF] text-[#011E38]'
                      : 'text-gray-700 hover:bg-gray-50'
                  )}
                >
                  <span>{bank.name}</span>
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </button>
              );
            })
          ) : (
            <div className="p-4 text-center text-xs text-gray-400">
              No banks matching "{searchTerm}"
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NetBanking;
